import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { OptionRowsSkeleton } from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useServerTable } from '@/hooks/use-server-table';
import { MbmProjectHistoryDialog } from '../../components/mbm-project-history-dialog/mbm-project-history-dialog';
import { DEFAULT_MBM_PROJECTS_ORDER } from '../../graphql/queries';
import { createMbmProjectTableColumns } from '../../components/mbm-project-table-columns/mbm-project-table-columns';
import { useGetMbmCustomers, useGetMbmProjects } from '../../hooks/use-mbm-projects';
import {
  MbmCustomer,
  MbmProject,
  MbmProjectsPagingResult,
  MbmProjectStatusFilter,
} from '../../types/mbm-project.types';

const ALL_CUSTOMERS = 'all';

/**
 * Settings › Projects › MBM Projects (HR Module Navigation Map; agreed 2 Nov 2026): the projects synced
 * from MBM, read-only. Search, customer and status filters, and each project's history.
 */
export const MbmProjectsPanel = () => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [customerId, setCustomerId] = useState(ALL_CUSTOMERS);
  const [status, setStatus] = useState<MbmProjectStatusFilter>('all');
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 25,
    defaultOrder: DEFAULT_MBM_PROJECTS_ORDER,
  });
  const [historyState, setHistoryState] = useState<{ open: boolean; project: MbmProject | null }>({
    open: false,
    project: null,
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedKeyword(keyword.trim()), 600);
    return () => clearTimeout(timeout);
  }, [keyword]);

  useEffect(() => {
    resetPage();
  }, [debouncedKeyword, customerId, status, resetPage]);

  const { data: customersData, isLoading: isCustomersLoading } = useGetMbmCustomers();
  const customers = (customersData as MbmCustomer[] | undefined) ?? [];
  const { data, isLoading, error } = useGetMbmProjects({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search: debouncedKeyword,
    customerId: customerId === ALL_CUSTOMERS ? undefined : customerId,
    status,
    filter: columnWhere,
    order,
  });
  const result = (data as { MbmProjects?: MbmProjectsPagingResult })?.MbmProjects;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = createMbmProjectTableColumns({
    t,
    // Read-only: no Edit, no Activate / Deactivate.
    onEdit: () => undefined,
    onToggleStatus: () => undefined,
    onViewHistory: (project) => setHistoryState({ open: true, project }),
    canUpdate: false,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder={t('MBM_PROJECT_SEARCH_PLACEHOLDER')}
          aria-label={t('SEARCH')}
          className="h-9 w-full sm:w-72"
        />
        <Select value={customerId} onValueChange={setCustomerId}>
          <SelectTrigger className="h-9 w-full sm:w-56" aria-label={t('CUSTOMER')}>
            <SelectValue placeholder={t('CUSTOMER')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CUSTOMERS}>{t('MBM_PROJECT_ALL_CUSTOMERS')}</SelectItem>
            {isCustomersLoading && <OptionRowsSkeleton rows={3} />}
            {customers.map((customer) => (
              <SelectItem key={customer.CustomerId} value={customer.CustomerId}>
                {customer.CustomerName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(value) => setStatus(value as MbmProjectStatusFilter)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44" aria-label={t('STATUS')}>
            <SelectValue placeholder={t('STATUS')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('MBM_PROJECT_ALL_STATUSES')}</SelectItem>
            <SelectItem value="active">{t('ACTIVE')}</SelectItem>
            <SelectItem value="inactive">{t('INACTIVE')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{ ...pagination, totalCount }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <MbmProjectHistoryDialog
        open={historyState.open}
        onOpenChange={(open) =>
          setHistoryState((prev) => ({ open, project: open ? prev.project : null }))
        }
        project={historyState.project}
      />
    </div>
  );
};
